/**
 * @module Auth0ManagementApiBrowser
 * @description A utility class to facilitate communication with the Auth0 Management API for typical web app use cases
 */
/* eslint-disable import/extensions */
/* eslint-disable no-unused-vars */
import Auth0Client from './auth0-client-utils.mjs';
import Auth0CodedError from './auth0-coded-error.mjs';
import Auth0Constants from './auth0-constants.mjs';
import Auth0ScriptLoader from './auth0-dynamic-script-loader.mjs';
import Auth0IdentityProvider from './auth0-identity-provider.mjs';
import Auth0ManagementApiError from './auth0-localized-error.mjs';
import Auth0SentryHelper from './logging/auth0-sentry-helper.mjs';
import Auth0SupportedIdps from './auth0-supported-idps.mjs';
import Auth0UserSessionHelper from './auth0-session-helper.mjs';
import Auth0Utils from './auth0-utils-purejs.mjs';
import Auth0UtilsBrowser from './auth0-utils-browser.mjs';
import Auth0dIdpConfig from '../config/auth-idp-config.mjs';
import httpHelper from './auth0-http-helper.mjs';
import logger from './logging/auth0-logger.mjs';

class Auth0ManagementApiBrowser {
  /**
   * @constructor
   * @description A wrapper class to facilitate interaction with the Auth0 Management API ({@link https://auth0.com/docs/api/management/v2/introduction |https://auth0.com/docs/api/management/v2/introduction})
   * @param {object} options Auth0 Management API interface options
   * @param {(string|Auth0Tenant|Auth0Domain)} options.tenantOrDomain The shipstation.com signin domain or the tenant (e.g. signin.shipstation.com, signin-stage.shipstation.com, shipstation-stage, etc)
   * @param {(string|Auth0Client)} options.clientId The client ID of the Auth0 application starting the auth flow (e.g. mvUDKj8esZ2DFqLjEKzmta5rJEhXHbCP)
   * @param {string} options.authServiceBaseUrl When initialized in the context of "admin" support, this library has a reliance on the ShipStation Auth Service. If not provided, the nodejs server is assumed to be at window.location.origin
   * @param {function} [options.auth0SpaJsCreateClient] This is a function to create a new auth0-spajs client. If you have access to the `auth0-spajs` package, you can pass in its provided `createAuth0Client()` func. If the `auth0-spajs` package is already loaded into the dom, then this arg can be omitted
   * @param {object} [options.sentry] An optional sentry reporter. Typically only for use in nodejs since it's very difficult to instantiate 2 different sentry clients (they fight). This allow for createUser to use the server's existing instance.
   * @param {class} [options.dataRepository] Only intended for use by testing frameworks. Class extending Auth0HttpHelper
   * @param {string} options.sumoLogicEndpoint An optional collector endpoint for sumo.
   */
  constructor(options) {
    // Base class initialization (merged from Auth0ManagementApiBase)
    if (!options.tenantOrDomain) {
      throw new Error('The Auth0 Management API must be initialized with a tenant or domain');
    }

    const resolved = Auth0Utils.tenantOrDomainResolver(options.tenantOrDomain);
    this._tenantOrDomain = options.tenantOrDomain;
    this._domain = resolved.domain;
    this._tenant = resolved.tenant;

    if (options.clientId) {
      this._client = typeof options.clientId === 'string'
        ? new Auth0Client(options.clientId, options.clientSecret)
        : options.clientId;
    }

    this._idpConfig = new Auth0dIdpConfig({ tenantOrDomain: this._tenant.id });
    this._http = options.dataRepository ? options.dataRepository : httpHelper;

    if (options.sentry) {
      this._sentry = new Auth0SentryHelper({ sentry: options.sentry });
    }

    this._underTest = !!options.dataRepository;
    console.log(`Auth0ManagementApi: created for tenant ${this._tenant.id} and client ${this._client?.clientId || 'none'}`);

    // Browser-specific initialization
    this._initLogger(options);
    this._init(options);

    this._idpHelper = new Auth0SupportedIdps({ tenantOrDomain: this._tenant.id });
  }

  /**
   * The Auth0Domain object associated with this API instance {@link module:Auth0Domain|Auth0Domain}.
   * @readonly
   * @instance
   * @memberof Auth0ManagementApiBrowser
   */
  get domain() {
    return this._domain;
  }

  /**
   * The Auth0Tenant object associated with this API instance {@link module:Auth0Tenant|Auth0Tenant}.
   * @readonly
   * @instance
   * @memberof Auth0ManagementApiBrowser
   */
  get tenant() {
    return this._tenant;
  }

  /**
   * The Auth0Client object associated with this API instance {@link module:Auth0Client|Auth0Client}.
   * @readonly
   * @instance
   * @memberof Auth0ManagementApiBrowser
   */
  get client() {
    return this._client;
  }

  _init = (options) => {
    if (!options.auth0SpaJsCreateClient) {
      if (window.createAuth0Client) {
        const v1Msg = 'Auth0ManagementApiBrowser: Auth0SpaJs createAuth0Client is present, but cannot be used because this package is not compatible with Auth0 SpaJs v1... MUST BE FROM AUTH0SPAJS v2. Proceeding to load v2 would overwrite the v1 version and maybe break your app.';
        console.log(v1Msg);
        throw new Error(v1Msg);
      } else if (window.auth0?.createAuth0Client) {
        console.log('Auth0ManagementApiBrowser: Auth0SpaJs v2 createAuth0Client is already present, using this...');
        this._createAuth0Client = window.auth0.createAuth0Client;
      } else {
        // last resort, load it ourselves from cdn
        console.log('Auth0ManagementApiBrowser: No Auth0SpaJs provided, flagging for internal v2 dynamic load');
      }
    } else {
      console.log('Auth0ManagementApiBrowser: Auth0SpaJs createAuth0Client provided by consumer, using this... MUST BE FROM AUTH0SPAJS v2');
      this._createAuth0Client = options.auth0SpaJsCreateClient;
    }

    if (options.authServiceBaseUrl) {
      this._authServiceBaseUrl = options.authServiceBaseUrl.replace(/\/$/, '');
      console.log(`Auth0ManagementApiBrowser: Auth service found => ${this._authServiceBaseUrl}`);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Link a secondary identity to a primary account via SSAS proxy.
   *
   * Supports two modes:
   * 1. Admin mode: Provide primaryUserId + secondaryUserId. Admin JWT proves authorization.
   * 2. Self-service mode: Provide primaryUserJwt + secondaryUserJwt. Both JWTs prove ownership.
   *
   * @param {object} linkOptions Account linking options
   * @param {string} [linkOptions.primaryUserId] The user ID of the primary Auth0 user (admin mode)
   * @param {string} [linkOptions.secondaryUserId] The user ID of the secondary Auth0 user (admin mode)
   * @param {string} [linkOptions.primaryUserJwt] SS user's JWT - proves ownership of primary account (self-service mode)
   * @param {string} [linkOptions.secondaryUserJwt] SSO user's JWT - proves ownership of secondary identity (self-service mode)
   * @returns {Promise<object>} Response containing success status, primaryUserId, and secondaryUserId
   */
  linkUsers = async (linkOptions) => {
    const {
      primaryUserId, secondaryUserId, primaryUserJwt, secondaryUserJwt,
    } = linkOptions;

    // Self-service mode: both JWTs provided for dual validation
    if (primaryUserJwt && secondaryUserJwt) {
      return this._linkViaSsas({
        primaryUserId: Auth0UtilsBrowser.getJwtBodyFromToken(primaryUserJwt).sub,
        secondaryUserId: Auth0UtilsBrowser.getJwtBodyFromToken(secondaryUserJwt).sub,
        primaryUserJwt,
        secondaryUserJwt,
      });
    }

    // Admin mode: explicit user IDs, admin JWT from current session
    if (primaryUserId && secondaryUserId) {
      return this._linkViaSsas({ primaryUserId, secondaryUserId });
    }

    throw new Error('linkUsers requires either (primaryUserJwt + secondaryUserJwt) for self-service '
      + 'or (primaryUserId + secondaryUserId) for admin mode');
  };

  /**
   * @function
   * @async
   * @private
   * @description Internal method to link accounts via SSAS. Handles both admin and self-service modes.
   * @param {object} options Linking options
   * @param {string} options.primaryUserId The primary user ID
   * @param {string} options.secondaryUserId The secondary user ID
   * @param {string} [options.primaryUserJwt] For self-service: primary user's JWT (ID token with aud=clientId)
   * @param {string} [options.secondaryUserJwt] For self-service: secondary user's JWT (ID token)
   * @returns {Promise<object>} Response from SSAS
   */
  _linkViaSsas = async (options) => {
    const {
      primaryUserId, secondaryUserId, primaryUserJwt, secondaryUserJwt,
    } = options;
    const isSelfService = !!(primaryUserJwt && secondaryUserJwt);

    console.log(`Auth0ManagementApiBrowser: _linkViaSsas (${isSelfService ? 'self-service' : 'admin'}) `
      + `primary=${primaryUserId} secondary=${secondaryUserId}`);

    if (!this._isUserIdWellFormed(secondaryUserId)) {
      throw new Error(`Cannot link users! Secondary user ID ${secondaryUserId} is not well formed.`);
    }

    const normalizedPrimaryUserId = this._normalizeAuth0PrimaryId(primaryUserId);
    const linkUrl = `${this._resolveAuthServiceBaseUrl()}/user/link`;

    // Self-service: primary JWT in Authorization header (authenticates caller).
    // ID tokens have aud=clientId which SSAS uses for tenant resolution.
    // Both JWTs are in body for SSAS to validate ownership of both accounts.
    if (isSelfService) {
      return this._http.postJson({
        url: linkUrl,
        body: {
          primaryUserId: normalizedPrimaryUserId,
          secondaryUserId,
          primaryUserJwt,
          secondaryUserJwt,
        },
        contextKey: 'error-idp-linking',
        jwt: primaryUserJwt,
      });
    }

    // Admin: send user IDs in body, admin JWT in Authorization header
    const idpId = this._getIdpIdFromUserId(secondaryUserId);
    const idpName = this._idpHelper.getIdpNameById(idpId);
    let adminToken;
    try {
      adminToken = await this._getAuth0ManagementProxyApiToken();
    } catch (e) {
      throw new Auth0ManagementApiError(e, 'error-idp-linking', Auth0Constants.ERROR_CODE_NO_MGMT_TOKEN, idpName);
    }

    return this._http.postJson({
      url: linkUrl,
      body: {
        primaryUserId: normalizedPrimaryUserId,
        secondaryUserId,
      },
      contextKey: 'error-idp-linking',
      jwt: adminToken.jwt,
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Unlink a secondary identity from a primary account via SSAS proxy.
   *
   * Supports two modes:
   * 1. Admin mode: Provide primaryUserId. Admin JWT proves authorization.
   * 2. Self-service mode: No primaryUserId. Current user's JWT proves ownership.
   *
   * @param {object} unlinkOptions Account unlinking options
   * @param {string} unlinkOptions.secondaryUserId The user ID of the secondary user (e.g. google-oauth2|1234567890) to unlink
   * @param {string} [unlinkOptions.primaryUserId] The user ID of the primary Auth0 user (admin mode). If omitted, uses current user (self-service mode).
   * @param {boolean} [unlinkOptions.unlinkAllUsers] If true, unlink ALL secondary identities from the primary user
   * @returns {Promise<object>} Response containing success status and updated identities
   */
  unlinkUsers = async (unlinkOptions) => {
    const { primaryUserId, secondaryUserId, unlinkAllUsers } = unlinkOptions;

    // Handle "unlink all" case
    if (unlinkAllUsers) {
      return this._unlinkAllUsers(primaryUserId);
    }

    // Single unlink
    if (!this._isUserIdWellFormed(secondaryUserId)) {
      throw new Error(`Cannot unlink users! Secondary user ID ${secondaryUserId} is not well formed.`);
    }

    return this._unlinkViaSsas({ primaryUserId, secondaryUserId });
  };

  /**
   * @function
   * @async
   * @private
   * @description Unlink ALL secondary identities from a user. Handles both admin and self-service modes.
   * @param {string} [primaryUserId] The primary user ID (admin mode). If omitted, uses current user (self-service mode).
   * @returns {Promise<object>} Map of secondaryUserId → unlink result
   */
  _unlinkAllUsers = async (primaryUserId) => {
    // Get current links - pass primaryUserId for admin mode, omit for self-service
    const options = primaryUserId ? { primaryUserId } : null;
    let currentLinks = await this.getUserIdentities(options);
    currentLinks = currentLinks.filter(link => link.isSocial);

    if (currentLinks.length === 0) return {};

    const mode = primaryUserId ? 'admin' : 'self-service';
    console.log(`_unlinkAllUsers (${mode}) => unlinking ${currentLinks.length} identities...`);

    // Important: unlink serially or Auth0 gets confused
    const unlinkResponses = {};
    for (let i = 0; i < currentLinks.length; i++) {
      const link = currentLinks[i];
      console.log(`_unlinkAllUsers => unlinking ${link.user_id}...`);
      // eslint-disable-next-line no-await-in-loop
      const unlinkResult = await this._unlinkViaSsas({
        primaryUserId,
        secondaryUserId: link.user_id,
      });
      unlinkResponses[link.user_id] = unlinkResult;
    }

    return unlinkResponses;
  };

  /**
   * @function
   * @async
   * @private
   * @description Internal method to unlink accounts via SSAS. Handles both admin and self-service modes.
   * @param {object} options Unlinking options
   * @param {string} options.secondaryUserId The secondary user ID to unlink
   * @param {string} [options.primaryUserId] The primary user ID (admin mode). If omitted, derived from current user's JWT (self-service mode).
   * @returns {Promise<object>} Response from SSAS
   */
  _unlinkViaSsas = async (options) => {
    const { secondaryUserId } = options;
    let { primaryUserId } = options;
    const isSelfService = !primaryUserId;

    // Get user's JWT (needed for both modes - as auth for admin, as auth + ID source for self-service)
    const idpId = this._getIdpIdFromIdpUserId(secondaryUserId);
    const idpName = this._idpHelper.getIdpNameById(idpId);
    let userToken;
    try {
      userToken = await this._getAuth0ManagementProxyApiToken();
    } catch (e) {
      throw new Auth0ManagementApiError(e, 'error-idp-unlinking', Auth0Constants.ERROR_CODE_NO_MGMT_TOKEN, idpName);
    }

    // Self-service: derive primaryUserId from JWT
    if (isSelfService) {
      const claims = Auth0UtilsBrowser.getJwtBodyFromToken(userToken.jwt);
      primaryUserId = claims.sub;
    }

    const normalizedPrimaryUserId = this._normalizeAuth0PrimaryId(primaryUserId);

    console.log(`Auth0ManagementApiBrowser: _unlinkViaSsas (${isSelfService ? 'self-service' : 'admin'}) `
      + `primary=${normalizedPrimaryUserId} secondary=${secondaryUserId}`);

    const unlinkUrl = `${this._resolveAuthServiceBaseUrl()}/user/link`;

    return this._http.deleteJson({
      url: unlinkUrl,
      body: {
        primaryUserId: normalizedPrimaryUserId,
        secondaryUserId,
      },
      contextKey: 'error-idp-unlinking',
      jwt: userToken.jwt,
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A wrapper for the Auth0 Management API's {@link https://auth0.com/docs/api/management/v2/users/get-users-by-id |user fetch functionality}. The currently logged in ShipStation user is the implied target (unless primaryUserId is supplied)
   * @param {object} [options] Optional unless fetching user data as admin
   * @param {string} [options.primaryUserId] The optional Auth0 user ID. If not provided, the ID of the currently authenticated user will be used. Note! Only administrative M2M requests can supply the primaryUserId. Any use of this function from a browser MUST use the currently authenticated user ID.
   * @param {string} [options.jwt] The ShipStation JWT used in the request context (required for admin-based actions)
   * @returns {(Promise|object)} A promise object whose resolution represents completion of the fetch user request. Said promise resolves to an Auth0 user object.
   */
  getUser = async (options = null) => {
    return options?.primaryUserId
      ? this._getUserAsAdmin(options.primaryUserId)
      : this._getUserAsSelf();
  };

  /**
   * @function
   * @async
   * @private
   * @description A wrapper for the Auth0 Management API's {@link https://auth0.com/docs/api/management/v2/users/get-users-by-id |user fetch functionality} intended for use by ShipStation staff. This will allow an admin user to obtain information about other users.
   * @param {string} primaryUserId The Auth0 user ID.
   * @returns {(Promise|object)} A promise object whose resolution represents completion of the fetch user request. Said promise resolves to an Auth0 user object.
   */
  _getUserAsAdmin = async (primaryUserId) => {
    console.log(`Auth0ManagementApiBrowser: _getUserAsAdmin request for id ${primaryUserId}`);
    let a0MgmtToken;
    try {
      a0MgmtToken = await this._getAuth0ManagementProxyApiToken();
    } catch (e) {
      throw new Auth0ManagementApiError(e, 'error-idp-fetch-user', Auth0Constants.ERROR_CODE_NO_MGMT_TOKEN);
    }

    primaryUserId = this._normalizeAuth0PrimaryId(primaryUserId);
    const fetchUserUrl = `${this._resolveAuthServiceBaseUrl()}/user/${primaryUserId}`;
    console.log(`send fetch user request to ${fetchUserUrl}`);

    return this._http.getJson({
      url: fetchUserUrl,
      contextKey: 'error-idp-fetch-user',
      jwt: a0MgmtToken.jwt,
    });
  };

  /**
   * @function
   * @async
   * @private
   * @description A wrapper for the Auth0 Management API's {@link https://auth0.com/docs/api/management/v2/users/get-users-by-id |user fetch functionality} for the currently logged in ShipStation user (only)
   * @returns {(Promise|object)} A promise object whose resolution represents completion of the fetch user request. Said promise resolves to an Auth0 user object.
   */
  _getUserAsSelf = async () => {
    console.log('Auth0ManagementApiBrowser: _getUserAsSelf request');

    // attempts to get the user profile from the Auth0 in different ways depending on browser state
    // Attempt 1 - direct call to Auth0 Management API. User must be able to fetch an auth0 mgmt api token
    try {
      const user = await this._getUserAsSelfViaDirectAuth0MgmtApi();
      console.log(`Auth0ManagementApiBrowser: _getUserAsSelfViaDirectAuth0MgmtApi success => ${JSON.stringify(user)}`);
      return user;
    } catch (error) {
      console.log('Auth0ManagementApiBrowser: _getUserAsSelfViaDirectAuth0MgmtApi failed, trying ss auth0 proxy...');
    }

    // Attempt 2 - call to ShipStation Auth0 Proxy API
    try {
      const user = await this._getUserAsSelfViaShipStationAuth0Proxy();
      console.log(`Auth0ManagementApiBrowser: _getUserAsSelfViaShipStationAuth0Proxy success => ${JSON.stringify(user)}`);
      return user;
    } catch (error) {
      console.log('Auth0ManagementApiBrowser: _getUserAsSelfViaShipStationAuth0Proxy failed...');
      throw new Error(`Unable to fetch user profile. ${error.message}`);
    }
  };

  _getUserAsSelfViaDirectAuth0MgmtApi = async () => {
    console.log('Auth0ManagementApiBrowser: _getUserAsSelfViaDirectAuth0MgmtApi request');
    let a0MgmtToken = {};
    try {
      a0MgmtToken = await this._getAuth0ManagementApiToken();
    } catch (e) {
      if (!this._underTest) throw new Auth0ManagementApiError(e, 'error-idp-fetch-user', Auth0Constants.ERROR_CODE_NO_MGMT_TOKEN);
    }

    const fetchUserUrl = `https://${this._tenant.resolveDomain()}/api/v2/users/${a0MgmtToken?.user?.sub}`;
    console.log(`send fetch user request to ${fetchUserUrl}`);

    const a0User = await this._http.getJson({
      url: fetchUserUrl,
      contextKey: 'error-idp-fetch-user',
      jwt: a0MgmtToken.jwt,
      withCredentials: false,
    });
    return this._normalizeUserIds(a0User);
  };

  _getUserAsSelfViaShipStationAuth0Proxy = async () => {
    let { userId } = Auth0UserSessionHelper;
    let { userAccessToken } = Auth0UserSessionHelper;
    if (!userId || !userAccessToken) {
      console.log('Auth0ManagementApiBrowser: _getUserAsSelfViaShipStationAuth0Proxy missing localstorage data, request token from auth0');
      const proxyTokenData = await this._getAuth0ManagementProxyApiToken();
      console.log(`Auth0ManagementApiBrowser: _getUserAsSelfViaShipStationAuth0Proxy token data => ${JSON.stringify(proxyTokenData)}`);
      userAccessToken = proxyTokenData.jwt;
      const allClaims = Auth0UtilsBrowser.getJwtBodyFromToken(userAccessToken);
      userId = allClaims.sub;

      if (!userId || !userAccessToken) {
        throw new Error('Unable to fetch user profile. User is not authenticated.');
      }
    }

    const currentUserId = userId;
    console.log('Auth0ManagementApiBrowser: _getUserAsSelfViaShipStationAuth0Proxy request =>', currentUserId);
    const fetchUserUrl = `${this._resolveAuthServiceBaseUrl()}/user/${currentUserId}`;
    console.log(`send fetch user request to ${fetchUserUrl}`);

    const a0User = await this._http.getJson({
      url: fetchUserUrl,
      contextKey: 'error-idp-fetch-user',
      jwt: userAccessToken,
    });
    return this._normalizeUserIds(a0User);
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A wrapper for the Auth0 Management API's {@link https://auth0.com/docs/api/management/v2/users/post-users |Create a user}. Make a new user in the Central-Connection database. Available from the browser (new user onboard) and from NodeJS.
   * @param {object} options Required
   * @param {string} options.source Required. Either "form" or "sso". This param determines if the new user data should be taken directly from other params (e.g. username, email, etc) because the user entered this data into a form or if the user wants to create a new user based on authentication with an IDP (SSO, like google, etc). In that case, new user data shall be inferred based on the user's IDP profile.
   * @param {object} options.idpUserProfile Required if "source" is set to "sso". This is the primary source of data for new user data
   * @param {string} options.idpId Required if "source" is set to "sso". The Auth0-based id of the IDP used for authentication.
   * @param {string} options.password Required if "source" is set to "form". The desired plaintext password for the new user (will be encrypted prior to storage)
   * @param {string} options.email Required if "source" is set to "form", optional otherwise. The new user's email address. By default, this must unique as it will become the new user's username. It will also be used for new account verification via email. For SSO source users, supplying this value will be considered and override and a value will NOT be derived using the IdP profile.
   * @param {string} [options.username] Optional. With it, you can set the new user's username to something other than the supplied email address.
   * @param {string} [options.name] Optional. The new user's full name. For SSO source users, supplying this value will be considered and override and a value will NOT be derived using the IdP profile.
   * @param {string} [options.givenName] Optional. The new user's first name. For SSO source users, supplying this value will be considered and override and a value will NOT be derived using the IdP profile.
   * @param {string} [options.familyName] Optional. The new user's last name. For SSO source users, supplying this value will be considered and override and a value will NOT be derived using the IdP profile.
   * @param {string} [options.countryCode] Optional. The new user's country code. For SSO source users, supplying this value will be considered and override and a value will NOT be derived using the IdP profile.
   * @param {string} [options.requestedLanguage] Optional. If specified for an SSO source user, the value will be considered an override and the IDP profile will not be referenced.
   * @param {string} [options.connection="Central-Connection"] Optional. The name of the database connection in which to create the new user.
   * @param {string} [options.ioBlackBoxToken] Anti-fraud Iovation BlackBox token. See SS marketing site for example generation
   * @param {string} [options.registrationChannel] The channel used for registration. Not persisted, only used to bypass security checks where use of captcha is not possible. Omitting this value subjects onboarding to captcha. See {@link https://app.launchdarkly.com/default/production/features/enable-captcha-validation-for-ship-station-registration-channel/targeting}
   * @param {string} [options.captchaToken] The captcha token is required if the `registrationChannel` is not present in the following LD flag. See {@link https://app.launchdarkly.com/default/production/features/enable-captcha-validation-for-ship-station-registration-channel/targeting}. Currently, this token must be Google reCaptcha v3 type.
   * @param {string} [options.hubSpotCookie] The optional tracking cookie used for HubSpot correlation.
   * @param {string} [options.ipAddress] Optional. Indicate the IP address from where the SSO signup button was clicked.
   * @returns {(Promise|object)} A promise object whose resolution represents completion of the new user request. Said promise resolves to an Auth0 user object.
   */
  createUser = async (options) => {
    console.log(`Auth0ManagementApiBrowser: createUser for ${JSON.stringify(options)}`);
    const createUserUrl = `${this._resolveAuthServiceBaseUrl()}/user`;
    console.log(`send create user request to ${createUserUrl}`);

    return this._http.postJson({
      url: createUserUrl,
      body: options,
      contextKey: 'error-create-user',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A login preflight function that should be executed before all logins though its success is not necessary to continue with the login request. Its purpose is to aid in the propagation of user data to the Auth0 tenant for use in the login process (the custom DB login flow is notoriously limited in the data available for assessment prior to engaging IdentityService. This serves a general mechanism by which we can make other (non-secure) data available to that flow.
   * @param {object} options The following props are available for use in the pre-login flow
   * @param {string} options.username The username of the user starting log in flow
   * @param {string} options.captchaType Captcha type to use for login. Can be 'turnstile' or 'gcaptcha'
   * @param {string} options.secureConfigToken Signed JWT token containing the secure config data (can influence how auth behaves on the backend; signed by SS servers)
   * @param {string} options.captchaToken  The captcha token generated by the captcha widget
   * @param {string} options.workstationToken If present on the machine, the user's workstation token.
   * @param {object} options.whiteLabelOptions Any white label options for the requested app's login page
   * @param {string} options.originatingClientId The client ID of the app that initiated the login flow
   * @param {string} options.audience The audience for the login request
   * @param {string} options.nonce The Auth0 generated nonce for the login request
   * @param {string} options.redirect_uri The redirect URI for the login request
   * @param {string} options.response_type The response type for the login request, Typically 'code' for authorization code grant
   * @param {string} options.scope The scope for the login request
   * @param {string} options.state The Auth0 generated state for the login request
   * @param {string} options._csrf The Auth0 generated CSRF token for the login request
   * @returns {object} preLoginResponse The pre-login response object
   * @returns {bool} preLoginResponse.cancelLogin Returning true will cancel the login request (will not continue with auth0 login)
   * @returns {message} preLoginResponse.message The translation key entry or string explaining why login was cancelled
   */
  preLoginActions = async (options) => {
    console.log(`Auth0ManagementApiBrowser: preLoginActions for ${JSON.stringify(options)}`);
    const loginPreflightUrl = `${this._resolveAuthServiceBaseUrl()}/user/login`;
    console.log(`send preLoginActions user request to ${loginPreflightUrl}`);

    // temp: fix in SSAS... this is a fast fix for a seller
    try {
      options.username = options.username?.trim();
    } catch (error) {
      // ignore (extra safety)
    }

    return this._http.postJson({
      url: loginPreflightUrl,
      body: options,
      contextKey: 'error-user-login-proxy',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Progressive authentication: checks username to determine which authentication method to display (password, SSO, both, or auto-redirect). This is the identifier-first login flow where the user enters their email/username first, then the system determines how they should authenticate based on their account configuration.
   * @param {object} options - Options object
   * @param {string} options.username - Username or email address to check
   * @param {string} options.clientId - Auth0 client ID of the requesting application
   * @param {string} options.tenant - Auth0 tenant identifier (e.g. 'shipstation', 'shipstation-stage')
   * @returns {Promise<object>} authMethodResponse The authentication method determination
   * @returns {string} authMethodResponse.authMethod The authentication method to use. One of: 'password-only' (show password field only), 'sso-only' (show SSO buttons only), 'hybrid' (show both password and SSO), 'auto-sso' (immediately redirect to specific SSO), 'default' (show all options on error/unknown user)
   * @returns {boolean} authMethodResponse.requiresPassword Whether the password field should be displayed
   * @returns {string[]} authMethodResponse.allowedIdps Array of allowed identity provider connection names (e.g., ['google-oauth2', 'microsoft-oauth2']). Empty array [] means show all configured IdPs. Specific values filter which SSO buttons are visible.
   * @returns {string} [authMethodResponse.redirectIdp] Identity provider connection name to redirect to immediately. Only present when authMethod is 'auto-sso' (e.g., 'woot-oidc')
   */
  checkUsernameAuthMethod = async (options) => {
    console.log(`Auth0ManagementApiBrowser: checkUsernameAuthMethod for ${options.username}`);
    const checkUsernameUrl = `${this._resolveAuthServiceBaseUrl()}/user/check-username`;
    console.log(`check username auth method at ${checkUsernameUrl}`);
    return this._http.postJson({
      url: checkUsernameUrl,
      body: options,
      contextKey: 'error-check-username-auth-method',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Fetches the password policy for the tenant's database connection.
   * @returns {Promise<object>} The password policy configuration.
   */
  getPasswordPolicy = async () => {
    console.log('Auth0ManagementApiBrowser: getPasswordPolicy');
    // Build URL with tenant context via client_id query parameter
    // Try multiple sources for client ID: instance client, URL params, hash JWT tokens
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.slice(1));

    // DEBUG: Log all potential sources
    console.log('getPasswordPolicy DEBUG - window.location.href:', window.location.href);
    console.log('getPasswordPolicy DEBUG - window.location.search:', window.location.search);
    console.log('getPasswordPolicy DEBUG - window.location.hash:', window.location.hash);
    console.log('getPasswordPolicy DEBUG - this._client?.clientId:', this._client?.clientId);
    console.log('getPasswordPolicy DEBUG - urlParams.targetClientId:', urlParams.get('targetClientId'));
    console.log('getPasswordPolicy DEBUG - urlParams.client_id:', urlParams.get('client_id'));

    // Extract clientId from JWT tokens (continue_token in hash, session_token in query)
    let embeddedTokenClientId = null;
    const continueToken = hashParams.get('continue_token');
    const sessionToken = urlParams.get('session_token');
    console.log('getPasswordPolicy DEBUG - continueToken exists:', !!continueToken);
    console.log('getPasswordPolicy DEBUG - sessionToken exists:', !!sessionToken);

    const tokenToCheck = continueToken || sessionToken;
    if (tokenToCheck) {
      try {
        const payload = Auth0UtilsBrowser.getJwtBodyFromToken(tokenToCheck);
        console.log('getPasswordPolicy DEBUG - token payload:', JSON.stringify(payload));
        embeddedTokenClientId = payload?.clientId || null;
        console.log('getPasswordPolicy DEBUG - embeddedTokenClientId:', embeddedTokenClientId);
      } catch (e) {
        console.log('getPasswordPolicy DEBUG - token parse error:', e);
        // Invalid JWT, ignore
      }
    }

    const clientId = this._client?.clientId
      || urlParams.get('targetClientId')
      || urlParams.get('client_id')
      || embeddedTokenClientId;
    console.log('getPasswordPolicy DEBUG - final clientId:', clientId);
    const queryParams = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
    const fetchPolicyUrl = `${this._resolveAuthServiceBaseUrl()}/policy/passwordPolicy${queryParams}`;
    console.log(`fetch password policy at ${fetchPolicyUrl}`);
    return this._http.getJson({
      url: fetchPolicyUrl,
      contextKey: 'error-fetch-password-policy',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that verified a user's email address similar to verifyUserEmailAddress, but before doing so will validate that there is a valid verify email ticket that belongs to user with `username`. This function will verify the address in the Auth0 user profile and also in ShipStation (via IdentityService).
   * @param {object} options options
   * @param {string} options.username The username of the user for which to verify the email address.
   * @param {string} options.ticket The ticket to verify (UUID)
   */
  verifyUserEmailAddressTicket = async (options) => {
    console.log(`Auth0ManagementApiBrowser: verifyUserEmailAddressTicket for ${JSON.stringify(options)}`);
    let verifyEmailUrl = `${this._resolveAuthServiceBaseUrl()}/user/verifyEmailTicket`;
    console.log(`send verifyEmailUrl request to ${verifyEmailUrl}`);

    verifyEmailUrl = Auth0UtilsBrowser.addSearchParamToUrl(verifyEmailUrl, 'ticket', options.ticket);
    verifyEmailUrl = Auth0UtilsBrowser.addSearchParamToUrl(verifyEmailUrl, 'username', options.username);
    return this._http.postJson({
      url: verifyEmailUrl,
      body: options,
      contextKey: 'error-verify-user-email',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that sends a verification email (link) to the user's current email address
   * @param {object} options options
   * @param {string} options.username Verify the email attached to user with this username.
   * @param {string} options.appId Controls help address and social media links. Can be: app:ssv3, spp:ssv2 or app:se
   * @param {string} [options.redirectUrl] Optional. The URL to which the user will be redirected after clicking the verification link. If not provided the app's default login url will be used.
   * @param {string} options.jwt The JWT for the request
   */
  sendEmailAddressVerification = async (options) => {
    console.log(`Auth0ManagementApiBrowser: sendEmailAddressVerification for ${JSON.stringify(options)}`);
    const { userAccessToken } = Auth0UserSessionHelper;

    let verifyEmailUrl = `${this._resolveAuthServiceBaseUrl()}/user/verifyEmailTicket`;
    console.log(`send verifyEmailUrl request to ${verifyEmailUrl}`);

    verifyEmailUrl = Auth0UtilsBrowser.addSearchParamToUrl(verifyEmailUrl, 'ticket', options.ticket);
    verifyEmailUrl = Auth0UtilsBrowser.addSearchParamToUrl(verifyEmailUrl, 'username', options.username);
    return this._http.postJson({
      url: verifyEmailUrl,
      body: options,
      contextKey: 'error-verify-user-email',
      jwt: userAccessToken,
    });
  };

  // ===== START MFA ====================================================================================================================================
  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that will attempt to enroll the user in an MFA factor. A wrapper for the Auth0 Authentication API's {@link https://auth0.com/docs/api/authentication#add-an-authenticator |Enroll an MFA Factor}.
   * @param {object} options Enroll options
   * @param {string} options.type The type of enrollment. Must be `otp` or `oob` or `recovery-code`
   * @param {string} [options.oobChannel] The out of band channel. Required if type is `oob`. Must be one of `email, `sms`, `voice`, `auth0`
   * @param {string} [options.email] The email address of the user attempting to enroll. Required if oobChannel is `email`
   * @param {string} [options.phone] The phone number of the user attempting to enroll. Required if oobChannel is `sms` or `voice`
   * @param {string} [options.accessToken] This flow requires an Auth0 MFA token or a user access token to work properly. If a user is logged in, the current access token is preferred.
   * @param {string} [options.state] An optional state token from an Auth0 action redirect. If present will be placed into the context token before signing
   * @param {string} [options.mfaToken] This flow requires an Auth0 MFA token to work properly. This token can be negotiated outside this function if already in possession of one or, if not provided, this function will attempt to acquire one.
   * @param {string} [options.mfaGrantToken] If `mfaToken` is provided, this param is ignored. Otherwise, it is used to negotiate a new Auth0 MFA token.
   * @param {string} [options.password] Only required in order to get an MFA Token if the user specified in the `mfaGrantToken` token body has not logged in within the last MFA_SESSION_LENGTH_SEC seconds with username and password credentials.
   * @returns {object} enrollmentResponse The results of the enrollment.
   * @returns {string} enrollmentResponse.oobCode The Out of Band code that will required as an argument when verifying the user's email code
   * @returns {string} enrollmentResponse.mfaToken The MFA Token used for the enrollment.
   * @returns {string} enrollmentResponse.enrollmentContextToken This will be a newly signed token with the data found in the original, `mfaGrantToken` token body.
   */
  enrollMfa = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: enrollMfa for ${JSON.stringify(options)}`);
      const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/enroll`;
      console.log(`send mfa enroll request to ${mfaTokenUrl}`);

      const mfaTokenResponse = await this._http.postJson({
        url: mfaTokenUrl,
        body: options,
        contextKey: 'error-mfa-enroll',
      });
      console.log('enrollMfa END');
      return mfaTokenResponse;
    } catch (error) {
      console.log(`enrollMfa fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`enrollMfa failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to enroll MFA for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_ENROLL_FAIL);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that will attempt to enroll the user in the email MFA factor.
   * @param {object} options Enroll options
   * @param {string} [options.email] The email to enroll. If not provided, function will attempt to find email address in the MFA grant token body (jwt)
   * @param {string} [options.accessToken] This flow requires an Auth0 MFA token or a user access token to work properly.
   * @param {string} [options.mfaToken] This flow requires an Auth0 MFA token to work properly.
   * @param {string} [options.mfaGrantToken] If `mfaToken` is provided, this param is ignored. Otherwise, it is used to negotiate a new Auth0 MFA token.
   * @param {string} [options.password] Only required in order to get an MFA Token if the user specified in the `mfaGrantToken` token body has not logged in recently.
   * @returns {object} emailEnrollmentResponse The results of either enrollment or challenging, plus the MFA token used.
   * @returns {string} emailEnrollmentResponse.oobCode The Out of Band code that will required as an argument when verifying the user's email code
   * @returns {string} emailEnrollmentResponse.mfaToken The MFA Token used for the enrollment.
   */
  enrollEmailMfa = async (options) => {
    const emailEnrollResults = await this.enrollMfa({
      ...options,
      type: Auth0Constants.MFA_TYPE_OOB,
      oobChannel: Auth0Constants.MFA_OOB_CHANNEL_EMAIL,
    });
    return emailEnrollResults;
  };

  enrollAdditionalMfaFactor = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: enrollAdditionalMfaFactor for ${JSON.stringify(options)}`);
      const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/enrollAdditionalFactor`;
      console.log(`send mfa additional factor enroll request to ${mfaTokenUrl}`);

      const mfaTokenResponse = await this._http.postJson({
        url: mfaTokenUrl,
        body: options,
        contextKey: 'error-mfa-enroll-additional-factor',
      });
      console.log('enrollAdditionalMfaFactor END');
      return mfaTokenResponse;
    } catch (error) {
      console.log(`enrollAdditionalMfaFactor fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`enrollAdditionalMfaFactor failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to enroll MFA for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_ENROLL_FAIL);
    }
  };

  resendNewEnrollmentChallenge = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: resendNewEnrollmentChallenge for ${JSON.stringify(options)}`);
      const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/resendNewEnrollmentChallenge`;
      console.log(`send mfa additional factor enroll request to ${mfaTokenUrl}`);

      const mfaTokenResponse = await this._http.postJson({
        url: mfaTokenUrl,
        body: options,
        contextKey: 'error-mfa-enroll-additional-factor',
      });
      console.log('resendNewEnrollmentChallenge END');
      return mfaTokenResponse;
    } catch (error) {
      console.log(`resendNewEnrollmentChallenge fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`resendNewEnrollmentChallenge failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to enroll MFA for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_ENROLL_FAIL);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A combination wrapper that will detect if a user is already enrolled in email MFA and, if so, send a challenge request or if not, auto enroll user (which also sends a challenge request)
   * @param {object} options Enroll or challenge options
   * @param {string} [options.amfaFactorId] If already enrolled in the AMFA factor, the ID of the factor to challenge, otherwise, missing means to start enrollment
   * @param {string} [options.mfaToken] These flows require an Auth0 MFA token to work properly.
   * @param {string} [options.mfaGrantToken] The token passed to the code collection flow when AMFA starts
   * @param {string} [options.password] Only required in order to get an MFA Token if the user has not logged in recently with password credentials.
   * @returns {object} result The results of either enrollment or challenging, plus the MFA token used
   * @returns {string} result.oobCode The Out of Band code that will required as an argument when verifying the user's email code
   * @returns {string} result.mfaToken The MFA Token used for either the enrollment or challenge.
   */
  autoEnrollOrChallengeAmfaFactor = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: autoEnrollOrChallengeAmfaFactor for ${JSON.stringify(options)}`);
      const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/amfaEnrollOrChallenge`;
      console.log(`send amfa enroll/challenge request to ${mfaTokenUrl}`);

      const mfaTokenResponse = await this._http.postJson({
        url: mfaTokenUrl,
        body: options,
        contextKey: 'error-amfa-enroll-challenge',
      });
      console.log('autoEnrollOrChallengeAmfaFactor END');
      return mfaTokenResponse;
    } catch (error) {
      console.log(`autoEnrollOrChallengeAmfaFactor fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`autoEnrollOrChallengeAmfaFactor failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to enroll or challenge AMFA for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_ENROLL_FAIL);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that will attempt to challenge the user via the email MFA factor. A wrapper for the Auth0 Authentication API's {@link https://auth0.com/docs/api/authentication#challenge-request |Challenge an MFA Factor}.
   * @param {object} options Challenge options
   * @param {string} options.challengeType The type of challenge to invoke. Must be either `oob` or `otp`
   * @param {string} options.authenticatorId The (Auth0 ID) of the user's existing email enrollment.
   * @param {string} [options.state] An optional state token from an Auth0 action redirect.
   * @param {string} [options.mfaToken] This flow requires an Auth0 MFA token to work properly.
   * @param {string} [options.mfaGrantToken] If `mfaToken` is provided, this param is ignored. Otherwise, it is used to negotiate a new Auth0 MFA token.
   * @param {string} [options.password] Only required in order to get an MFA Token if the user has not logged in recently.
   * @returns {object} challengeResponse The results of the challenge, plus the MFA token used
   * @returns {string} challengeResponse.oobCode The Out of Band code that will required as an argument when verifying the user's email code
   * @returns {string} challengeResponse.mfaToken The MFA Token used for the challenge.
   */
  challengeMfa = async (options) => {
    const mfaChallengeUrl = `${this._tenant.auth0BasedDomain}/mfa/challenge`;
    console.log(`send challenge mfa request to ${mfaChallengeUrl}`);

    try {
      const jwt = await this._selectMfaAccessToken(options);

      const challengeResponse = await this._http.postJson({
        url: mfaChallengeUrl,
        body: {
          client_id: this._client.clientId,
          challenge_type: options.challengeType,
          authenticator_id: options.authenticatorId,
          mfa_token: jwt,
        },
        contextKey: 'error-challenge-mfa',
      });

      if (options.challengeType === Auth0Constants.MFA_TYPE_OOB && (!challengeResponse || !challengeResponse.oob_code)) {
        const errMsg = `Challenge type oob has no oob code. ${options.authenticatorId}.`;
        logger.error(errMsg);
        throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_CHALLENGE_OOB_CODE_MISSING);
      }
      return {
        oobCode: challengeResponse.oob_code,
        mfaToken: jwt,
      };
    } catch (error) {
      if (error instanceof Auth0CodedError) throw error; // throw as is
      const errMsg = `Failed to challenge MFA ID ${options.authenticatorId}. ${error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_CHALLENGE_OOB_CODE_MISSING);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Identical to `challengeMfa()` except the user's email enrollment ID will be determined automatically.
   * @param {object} options Challenge options
   * @param {string} [options.mfaToken] This flow requires an Auth0 MFA token to work properly.
   * @param {string} [options.mfaGrantToken] If `mfaToken` is provided, this param is ignored. Otherwise, it is used to negotiate a new Auth0 MFA token.
   * @param {string} [options.password] Only required in order to get an MFA Token if the user has not logged in recently.
   * @returns {object} challengeResponse The results of the challenge, plus the MFA token used
   * @returns {string} challengeResponse.oobCode The Out of Band code required for verification
   * @returns {string} challengeResponse.mfaToken The MFA Token used for the challenge.
   */
  challengeEmailMfa = async (options) => {
    // if not provided, fetch the email factor ID
    if (!options.authenticatorId) {
      try {
        console.log('challengeEmailMfa => no email authenticator ID provided, fetching...');
        const enrollmentsResponse = await this._getEmailMfaEnrollment(options);
        options.authenticatorId = enrollmentsResponse.emailFactorId;
        options.mfaToken = enrollmentsResponse.mfaToken;
      } catch (error) {
        const errMsg = `Failed to challenge email MFA because no email authenticator ID could be located. ${error.message}`;
        logger.error(errMsg);
        throw Error(errMsg);
      }
    }

    const challengeResponse = await this.challengeMfa({
      challengeType: Auth0Constants.MFA_TYPE_OOB,
      authenticatorId: options.authenticatorId,
      mfaToken: options.mfaToken,
    });
    return challengeResponse;
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function to verify the user's typed code entry for Out of Band verifications. A wrapper for the Auth0 Authentication API's {@link https://auth0.com/docs/api/authentication#verify-with-out-of-band-oob- |Verify an OOB Code}.
   * @param {object} options Challenge options
   * @param {string} options.mfaToken IMPORTANT: this token must be the SAME MFA TOKEN USED FOR THE CHALLENGE / ENROLLMENT FLOW.
   * @param {string} options.mfaGrantToken
   * @param {string} options.oobCode The OOB token received from the preceding enrollment or challenge
   * @param {string} options.bindingCode This is the verification code entered by the user, sent by Auth0 via the factor's transmission method (e.g. email, etc)
   * @param {boolean} [options.isForStepUp] - true if the request does not pertain to mfa settings
   * @returns {object} verifyResponse The code verification verification response
   * @returns {string} verifyResponse.accessToken The JWT verification token that needs to be passed back to Auth0 in order to continue the MFA flow
   */
  verifyOobMfaCode = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: verifyOobMfaCode for ${JSON.stringify(options)}`);
      const mfaVerifyCodeUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/verifyOobCode`;
      console.log(`send verify MFA code to ${mfaVerifyCodeUrl}`);

      const mfaVerifyCodeResponse = await this._http.postJson({
        url: mfaVerifyCodeUrl,
        body: options,
        contextKey: 'error-verify-oob-code',
      });
      console.log('verifyOobMfaCode END =>', JSON.stringify(mfaVerifyCodeResponse));
      return mfaVerifyCodeResponse;
    } catch (error) {
      console.log(`verifyOobMfaCode fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`verifyOobMfaCode failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to verify OOB code for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_VERIFY_FAILED);
    }
  };

  verifyMfaCode = async (options) => {
    try {
      const mfaVerifyCodeUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/verifyCode`;
      console.log(`send verify MFA code to ${mfaVerifyCodeUrl}`);

      const mfaVerifyCodeResponse = await this._http.postJson({
        url: mfaVerifyCodeUrl,
        body: options,
        contextKey: 'error-verify-code',
      });
      console.log('verifyMfaCode END =>', JSON.stringify(mfaVerifyCodeResponse));
      return mfaVerifyCodeResponse;
    } catch (error) {
      console.log(`verifyMfaCode fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`verifyMfaCode failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to verify code for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_VERIFY_FAILED);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Fetch a user's list of enrolled factors {@link https://auth0.com/docs/api/authentication#list-authenticators |List User's Enrolled Factors}.
   * @param {object} options request (one of the below tokens is required)
   * @param {string} [options.accessToken] This request requires an Auth0 MFA token or a user access token to work properly.
   * @param {string} [options.mfaToken] This request requires an Auth0 MFA token to work properly.
   * @param {string} [options.mfaGrantToken] If `mfaToken` is provided, this param is ignored.
   * @param {string} [options.password] Only required in order to get an MFA Token if the user has not logged in recently.
   * @returns {object} enrollmentsResponse The enrollments object
   * @returns {string} enrollmentResponse.mfaToken The MFA Token used
   * @returns {array} enrollmentsResponse.enrollments The array of enrollment objects for the user
   */
  getMfaEnrollmentsAsSelf = async (options) => {
    console.log('Auth0ManagementApiBrowser: getMfaEnrollments request');
    const mfaAuthenticatorsUrl = `${this._tenant.auth0BasedDomain}/mfa/authenticators`;
    console.log(`fetch mfaAuthenticators from ${mfaAuthenticatorsUrl}`);

    try {
      const jwt = await this._selectMfaAccessToken(options);
      const mfaAuthenticatorsResponse = await this._http.getJson({
        url: mfaAuthenticatorsUrl,
        contextKey: 'mfa-enrollments-fetch-fail',
        jwt,
      });
      return {
        enrollments: mfaAuthenticatorsResponse || [],
        mfaToken: jwt,
      };
    } catch (error) {
      logger.error(`Failed to fetch MFA authenticators. ${error.message}`);
      throw error;
    }
  };

  getMfaEnrollmentsForLogin = async (options) => {
    const enrollmentsUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/pre-login-authenticators`;

    const res = await this._http.postJson({
      url: enrollmentsUrl,
      body: options,
      contextKey: 'error-fetch-enrollments',
    });
    return res;
  };

  challengeMfaEnrollmentForLogin = async (options) => {
    const enrollmentsUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/pre-login-mfa-challenge`;

    const res = await this._http.postJson({
      url: enrollmentsUrl,
      body: options,
      contextKey: 'error-challenge-enrollment',
    });
    return res;
  };

  _getEmailMfaEnrollment = async (options) => {
    console.log('Auth0ManagementApiBrowser: _getEmailMfaEnrollment request');
    const authenticatorsResponse = await this.getMfaEnrollmentsAsSelf(options);
    const filtered = authenticatorsResponse.enrollments.filter(a => a.oob_channel === Auth0Constants.MFA_OOB_CHANNEL_EMAIL && a.active);
    if (filtered.length === 0) {
      const errMsg = `Unable to select an email factor, none exist. Propagate MFA token => ${authenticatorsResponse.mfaToken}`;
      console.log(errMsg);
      const err = new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_AMFA_FACTOR_NOT_FOUND);
      err.mfaToken = authenticatorsResponse.mfaToken;
      throw err;
    }
    return {
      emailFactorId: filtered.pop().id,
      mfaToken: authenticatorsResponse.mfaToken,
    };
  };

  getEnrollMfaTokenForMfaChallenge = async (options) => {
    // need try / catch?
    const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/startEnrollAdditionalFactor`;

    const startRes = await this._http.postJson({
      url: mfaTokenUrl,
      body: options,
      contextKey: 'error-verify-oob-code',
    });
    return startRes;
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that negotiates and returns an MFA token. An MFA token is a temporary token required in order to interact with the Auth0 MFA API. It is not considered valid for apps.
   * @param {object} options MFA token options
   * @param {string} [options.username] pass a username + password + originatingClientId if the request will be validated auth0 side
   * @param {string} options.mfaGrantToken Required for all versions. This is the token you'll need to get the real MFA token.
   * @param {string} [options.password] Only required in order to get an MFA Token if the user has not logged in recently.
   * @param {string} [options.originatingClientId] auth0 generated client id that is the source of the request
   * @param {string} [options.scope] Only required if the consumer needs to specify custom scopes not included by default.
   * @param {string} [options.audience] Only required if an audience other than the auth0 mfa api is needed
   * @param {boolean} [options.hasEnrollScope] true if token should have enroll authenticators scope
   * @param {boolean} [options.hasUnenrollScope] true if token should have remove authenticators scope
   * @param {boolean} [options.isForStepUp] True if the request is not associated with mfa settings e.g. a protected action in app like generating api keys
   * @returns {object} mfaTokens The MFA Token set
   * @returns {string} mfaTokens.accessToken The MFA access token (JWT)
   * @returns {string} mfaTokens.refreshToken The MFA refresh token (opaque)
   */
  getMfaTokens = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: getMfaTokens for ${JSON.stringify(options)}`);
      const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/token`;
      console.log(`send mfa token request to ${mfaTokenUrl}`);

      const mfaTokenResponse = await this._http.getJson({
        url: `${mfaTokenUrl}?${new URLSearchParams(options)}`,
        contextKey: 'error-mfa-initialization',
      });
      console.log('getMfaTokens END');
      return mfaTokenResponse;
    } catch (error) {
      console.log(`getMfaTokens tokens fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`getMfaTokens failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to fetch MFA token for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_INIT_MFA_TOKEN_FETCH_FAIL);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A function that negotiates and returns a user access token for use with MFA token. This token differs from the `getMfaTokens()` above in that it belongs to a currently authenticated user and just contains the `mfa` audience instead of `ss:webapi`. Since the user is already authenticated, this token is only useful for purposes of MFA administration.
   * @returns {string} The MFA access token string (a JWT)
   */
  getMfaUserAccessTokens = async () => {
    console.log('Auth0ManagementApiBrowser: start _getAuth0MfaApiToken');
    const scope = 'openid email profile offline_access enroll read:authenticators remove:authenticators';
    const options = {
      audience: `${this._tenant.auth0BasedDomain}/mfa/`,
      scope,
      ignoreCache: true,
    };
    try {
      const tokenData = await this._getAuth0ApiToken(options);

      return tokenData.jwt;
    } catch (error) {
      console.log(`getMfaAccessToken error => ${JSON.stringify(error)}`);
      throw error;
    }
  };

  _selectMfaAccessToken = async (options) => {
    // Helper function to auto select the appropriate MFA token
    // An MFA token can come from one of 3 different places:
    // 1. An MFA token is provided. Just return it
    // 2. An MFA grant token is provided. This is a token you can exchange for an Auth0 MFA Token usable with the Auth0 MFA API
    // 3. A user is currently logged in / authenticated and we can just exchange our current app access token for another access token, but with the /mfa audience instead
    if (options.mfaToken) {
      console.log('_selectMfaAccessToken => selected mfaToken');
      return options.mfaToken;
    }
    if (options.mfaGrantToken) {
      console.log('_selectMfaAccessToken => selected mfaGrantToken');
      const mfaTokens = await this.getMfaTokens(options);
      console.log('_selectMfaAccessToken got =>', JSON.stringify(mfaTokens));
      return mfaTokens.accessToken;
    }
    // otherwise see if we can get a user access token. It will fail if there is no current user auth
    console.log('_selectMfaAccessToken => selected mfaAccessToken');
    const userTokens = await this.getMfaUserAccessTokens();
    console.log('_selectMfaAccessToken got =>', JSON.stringify(userTokens));
    return userTokens.accessToken;
  };

  /**
   * @function
   * @async
   * @private
   * @description An internal helper function that will fetch a token with sufficient scope to do most basic user-based operations. This token will be requested using credentials for the currently authenticated user.
   * @param {string} [scope=openid email profile read:current_user read:user_idp_tokens update:current_user_identities delete:current_user] The optional scope. If not provided, the default will be used.
   * @returns {Object} An object with the resultant auth0Client (future use) and the jwt with requested scope
   */
  _getAuth0ManagementApiToken = async (scope = null) => {
    console.log('Auth0ManagementApiBrowser: start _getAuth0ManagementApiToken');
    scope = scope || 'openid email profile read:current_user read:user_idp_tokens update:current_user_identities delete:current_user';
    const tokenData = await this._getAuth0ApiToken({
      audience: `${this._tenant.auth0BasedDomain}/api/v2/`,
      scope,
      ignoreCache: true,
      useRefreshTokens: true,
    });

    const user = await tokenData.auth0Client.getUser();
    return { jwt: tokenData.jwt, user, auth0Client: tokenData.auth0Client };
  };

  _getAuth0MfaApiToken = async (scope = null) => {
    // This token
    console.log('Auth0ManagementApiBrowser: start _getAuth0MfaApiToken');
    scope = scope || 'openid email profile offline_access read:authenticators';
    const options = {
      audience: `${this._tenant.auth0BasedDomain}/mfa/`,
      scope,
      ignoreCache: true,
    };
    try {
      const tokenData = await this._getAuth0ApiToken(options);

      return tokenData.jwt;
    } catch (error) {
      console.log(`_getAuth0MfaApiToken error => ${JSON.stringify(error)}`);
      throw error;
    }
  };

  _getAuth0MfaUserApiToken = async () => {
    // This token can fetch available authenticators when the user account is MFA locked (so the user can be challenged)
    console.log('Auth0ManagementApiBrowser: start _getAuth0MfaUserApiToken');
    const tokenData = await this._getAuth0ApiToken({
      audience: Auth0Constants.AUTH0_DEFAULT_AUDIENCE,
      scope: 'openid mfa read:authenticators',
      ignoreCache: true,
    });

    return tokenData.jwt;
  };

  /**
   * @function
   * @async
   * @private
   * @description An internal helper function that will fetch a token that can be used to call on the Auth0 Management API proxy server (assuming role_set is super admin)
   * @returns {Object} An object with the resultant auth0Client (future use) and the jwt
   */
  _getAuth0ManagementProxyApiToken = async () => {
    console.log('Auth0ManagementApiBrowser: start _getAuth0ManagementProxyApiToken');
    const tokenData = await this._getAuth0ApiToken({
      audience: Auth0Constants.AUTH0_DEFAULT_AUDIENCE,
      scope: 'openid email profile',
    });

    return { jwt: tokenData.jwt, auth0Client: tokenData.auth0Client };
  };

  /**
   * @function
   * @async
   * @private
   * @description An internal helper function that will fetch a token for the specified audience with the specified scope
   * @param {object} options Options object required to init auth0 client and fetch token
   * @param {string} options.audience The audience for which to request a token
   * @param {string} options.scope The scope for which to request a token
   * @param {boolean} [options.ignoreCache] True if auth0 should be forced to fetch a new token remotely
   * @returns {Object} An object with the resultant auth0Client (future use) and the jwt
   */
  _getAuth0ApiToken = async (options) => {
    console.log(`Auth0ManagementApiBrowser: start _getAuth0ApiToken=>${options.audience}`);
    const a0Options = {
      domain: this._tenant.resolveDomain(),
      clientId: this._client.clientId,
      authorizationParams: {
        audience: options.audience,
        scope: options.scope,
      },
    };
    if (options.useRefreshTokens) {
      a0Options.cacheLocation = 'localstorage';
      a0Options.useRefreshTokens = true;
      a0Options.useRefreshTokensFallback = true;
    }

    console.log(`Create auth0 mgmt api client with options ${JSON.stringify(a0Options)}`);
    try {
      if (!this._createAuth0Client) {
        console.log('Auth0ManagementApiBrowser: this._createAuth0Client is empty, attempting to load dynamically...');
        const a0 = await this._loadAuth0SpaJs();
        this._createAuth0Client = a0.createAuth0Client;
      }
      const auth0Client = await this._createAuth0Client(a0Options);
      const jwt = await auth0Client.getTokenSilently({ cacheMode: options.ignoreCache ? 'off' : 'on' });
      console.log(`api token => ${jwt}`);

      return { auth0Client, jwt };
    } catch (e) {
      throw new Auth0ManagementApiError(e, 'error-auth0-mgmt-token', Auth0Constants.ERROR_CODE_MGMT_TOKEN_FAIL);
    }
  };

  _loadAuth0SpaJs = async () => {
    if (window.auth0) {
      return window.auth0;
    }

    console.log(`Auth0ManagementApiBrowser: Loading Auth0SpaJs v2 internally @ ${Auth0Constants.AUTH0_SPAJS_URL}`);
    const loader = new Auth0ScriptLoader(this._tenant.id);
    await loader.loadScripts(Auth0Constants.AUTH0_SPAJS_URL);
    console.log(`Auth0ManagementApiBrowser: Completed dynamic load of Auth0SpaJs from ${Auth0Constants.AUTH0_SPAJS_URL}`);
    return window.auth0;
  };

  _resolveAuthServiceBaseUrl = () => {
    return this._authServiceBaseUrl
      ? this._authServiceBaseUrl
      : window.location.origin;
  };

  optOutOfSmsEnrollment = async (options) => {
    try {
      console.log(`Auth0ManagementApiBrowser: optOutOfSmsEnrollment for ${JSON.stringify(options)}`);
      const mfaTokenUrl = `${this._resolveAuthServiceBaseUrl()}/mfa/optOutOfSmsEnrollment`;

      const optOutResponse = await this._http.postJson({
        url: mfaTokenUrl,
        body: options,
        contextKey: 'error-mfa-enroll-additional-factor',
      });
      return optOutResponse;
    } catch (error) {
      console.log(`optOutOfSmsEnrollment fail => ${JSON.stringify(error)}`);
      if (error.response && error.response.dataObj?.code) {
        logger.warn(`optOutOfSmsEnrollment failed => response code issued => ${error.response.dataObj.code} => ${error.response.data}`);
        throw new Auth0CodedError(error.response.dataObj.message, error.response.dataObj.code);
      }
      // otherwise, use generic failure code
      const errMsg = `Unable to opt out of sms enrollment for ${JSON.stringify(options)}. ${error.response?.data?.message || error.message}`;
      logger.error(errMsg);
      throw new Auth0CodedError(errMsg, Auth0Constants.MFA_ERROR_CODE_OPT_OUT_FAIL);
    }
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Fetch the IdP connection associated with an admin-generated linking token. This is an anonymous endpoint - no JWT required. The token UUID itself is the authorization.
   * @param {object} options Linking token options
   * @param {string} options.token UUID linking token generated by an admin
   * @returns {Promise<{idpConnection: string}>} IdP connection ID for the linking token
   */
  getLinkingTokenIdp = async (options) => {
    console.log(`Auth0ManagementApiBrowser: getLinkingTokenIdp for token ${options.token}`);
    const idpUrl = `${this._resolveAuthServiceBaseUrl()}/user/linking-token-idp/${options.token}`;

    return this._http.getJson({
      url: idpUrl,
      contextKey: 'error-get-linking-token-idp',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Consume an admin-generated linking token to link an SSO identity to a ShipStation account
   * @param {object} options Linking token consumption options
   * @param {string} options.token UUID linking token generated by an admin
   * @param {string} options.ssoUserId Auth0 user ID of SSO identity (e.g., "google-oauth2|123456789")
   * @param {string} options.ssoEmail Email address of SSO identity
   * @returns {Promise<object>} Response containing success status, primaryUserId, and secondaryUserId
   */
  consumeLinkingToken = async (options) => {
    console.log(`Auth0ManagementApiBrowser: consumeLinkingToken for token ${options.token}`);

    const consumeTokenUrl = `${this._resolveAuthServiceBaseUrl()}/user/consume-linking-token`;
    console.log(`send consume linking token request to ${consumeTokenUrl}`);

    // Get the current user's JWT to prove identity ownership
    // This JWT will have the SSO identity's sub claim (e.g., "google-oauth2|123456789")
    // User just authenticated via SSO, so token should be fresh in cache
    const { jwt } = await this._getAuth0ManagementProxyApiToken();
    console.log('consumeLinkingToken: obtained SSO user JWT from cache for authentication');

    return this._http.postJson({
      url: consumeTokenUrl,
      body: {
        token: options.token,
        ssoUserId: options.ssoUserId,
        ssoEmail: options.ssoEmail,
      },
      jwt, // Pass SSO user's JWT for authentication
      contextKey: 'error-consume-linking-token',
    });
  };

  // ===== UTILITY METHODS (merged from Auth0ManagementApiBase) ====================================================================================================================================

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A helper function that queries for an Auth0 user and then, in cases where you already know which identity you're looking for, filters out all user data except for that specific IdP's identity. The currently logged in ShipStation user is the implied target (unless primaryUserId is supplied).
   * @param {object} options Fetch options
   * @param {string} options.idpId The Auth0-based ID of the Identity Provider for which to search (in the user's existing profiles)
   * @param {string} [options.primaryUserId] The optional Auth0 user ID. If not provided, the ID of the currently authenticated user will be used.
   * @param {string} [options.jwt] The ShipStation JWT used in the request context (required for admin-based actions)
   * @returns {(Promise|array)} A promise object whose resolution represents completion of the fetch user request. Said promise resolves to an array of objects where each object represents a single primary or secondary (IdP SSO) identity.
   */
  getUserIdentityForId = async (options) => {
    const userData = await this.getUserIdentities(options);
    if (!userData) return {};

    return userData.filter(idpIdentity => idpIdentity.provider === options.idpId)?.pop() || {};
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description A helper function that queries for an Auth0 user and then filters out all user data except for the user's identity list (current SSO links). The currently logged in ShipStation user is the implied target (unless primaryUserId is supplied).
   * @param {object} [options] Optional unless fetching user data as admin
   * @param {string} [options.primaryUserId] The optional Auth0 user ID. If not provided, the ID of the currently authenticated user will be used.
   * @param {string} [options.jwt] The ShipStation JWT used in the request context (required for admin-based actions)
   * @returns {(Promise|array)} A promise object whose resolution represents completion of the fetch user request. Said promise resolves to an array of objects where each object represents a single primary or secondary (IdP SSO) identity.
   */
  getUserIdentities = async (options = null) => {
    const userData = await this.getUser(options);
    return userData?.identities || [];
  };

  /**
   * @function
   * @private
   * @description A simple internal helper function that will ensure an Auth0 primary ID is well formed
   * @param {string} id The primary user ID
   * @returns {string} The normalized primary user ID (starting with auth0|)
   */
  _normalizeAuth0PrimaryId = (id) => {
    const foundIdp = this._idpConfig.idps.find(idp => id.startsWith(`${idp.id}|`));
    if (foundIdp) {
      console.log(`IDP found for user ID ${id}:`, foundIdp);
      // id is normalized already but for an SSO provider
      return id;
    }

    return id.startsWith('auth0|')
      ? id
      : `auth0|${id}`;
  };

  /**
   * @function
   * @private
   * @description A simple internal helper function that will extract the IdP ID from an Auth0 user ID (that belongs to said IdP)
   * @param {string} id The user ID
   * @returns {string} The normalized user ID (starting with [idp-id]|)
   */
  _getIdpIdFromIdpUserId = (id) => {
    return Auth0IdentityProvider.getIdpIdFromUserId(id);
  };

  /**
   * @function
   * @private
   * @description A simple internal helper function that will extract the IdP ID from an Auth0 user ID (that belongs to said IdP)
   * @param {string} id The user ID
   * @returns {string} The normalized user ID (starting with [idp-name]|)
   */
  _getUserIdFromIdpUserId = (id) => {
    return Auth0IdentityProvider.getUserIdFromIdpUserId(id);
  };

  /**
   * @function
   * @private
   * @description A simple internal helper function that will check if a user ID is well formed using format [idp-id]|[user-id]
   * @param {string} id The user ID
   * @returns {boolean} True if well formed
   */
  _isUserIdWellFormed = (id) => {
    const idTokens = id?.split('|');
    return idTokens?.length === 2;
  };

  /**
   * @function
   * @private
   * @description An internal helper function normalizes user IDs from Auth0 such that they conform to the expected [idp-id]|[user-id] pattern. Auth0 is inconsistent about this...
   * @param {object} auth0UserObject The standard user object as returned by `this.getUser()`
   * @returns {object} The normalized user object (is also modified in place)
   */
  _normalizeUserIds = (auth0UserObject) => {
    if (!auth0UserObject || !auth0UserObject.identities) return;

    return Auth0IdentityProvider.normalizeIdpUserIdsForAuth0User(auth0UserObject);
  };

  _initLogger = (loggerOptions) => {
    logger.initialize({
      sumoEndpoint: loggerOptions.sumoLogicEndpoint,
      sourceName: 'Auth0ManagementApiBrowser',
    });
  };

  /**
   * @function
   * @async
   * @instance
   * @memberof Auth0ManagementApiBrowser
   * @description Requests a change-password email from SSAS. Replaces the previous
   * pattern of calling window.location.origin/user/requestChangePassword which routed
   * to the v3 node server — SSAS now owns this endpoint entirely.
   * @param {object} options
   * @param {string} options.username - The user's email/username
   * @param {string} options.client_id - Auth0 client ID (used by SSAS to resolve tenant class)
   * @param {string} [options.redirectUrl] - App login URL for post-change-password redirect
   * @param {string} [options.flowType] - 'user-initiated' or 'force-change'
   * @param {string} [options.whiteLabelOptionsJson] - Serialized white label config
   * @returns {Promise<object>}
   */
  startRequestChangePasswordFlow = async (options) => {
    console.log(`Auth0ManagementApiBrowser: startRequestChangePasswordFlow for ${options.username}`);
    const url = `${this._resolveAuthServiceBaseUrl()}/user/requestChangePassword`;
    return this._http.postJson({
      url,
      body: options,
      contextKey: 'change-password-invoked-fail',
    });
  };

  // DO NOT REMOVE; MUSTACHE VAR OVERRIDE; 
  get _version() {
    return '71c5d67';
  }
}

export default Auth0ManagementApiBrowser;
